// Docling escalation-lane tier gating — DISPLAY HINT ONLY.
//
// The frontend gates the lane picker (dialog engine selector + settings control)
// by mapping the user's tier (the server's best-of gate tier, falling back to the
// display `tierName` when none was sent) to the Docling escalation ceiling. This
// mirrors `internal/billing/tier.go`'s `DoclingVLMLevel` so the UI can show which
// lanes are unlocked. It is a HINT: the backend `docling.SelectMode` clamp is the
// authoritative gate. If this map drifts from the backend, a user might see an
// option they can select but the server clamps it down — safe, just cosmetically
// off, and self-correcting when the map is updated. NEVER treat this as a
// security boundary.

/** Per-document / per-user Docling lane. '' defers to the user's setting. */
export type DoclingVlmMode = '' | 'verify' | 'full' | 'ocr';

/**
 * Map a plan tier name (lowercase billing.Tier constant: minimum | standard |
 * plus | max | enterprise) to its Docling escalation ceiling (DoclingVLMLevel):
 * 0 none, 1 verify, 2 full, 3 mistral_ocr. Mirrors internal/billing/tier.go.
 * Unknown names fail low (0) — the safe default.
 */
export function tierNameToDoclingLevel(tierName: string): number {
  switch (tierName.trim().toLowerCase()) {
    case 'enterprise':
    case 'max':
    case 'plus':
      return 3;
    case 'standard':
      return 1;
    default: // 'minimum', 'free', '', unknown
      return 0;
  }
}

/** Minimum DoclingVLMLevel a lane requires. Mirrors SelectMode's requested-level map. */
export const LANE_MIN_LEVEL: Record<Exclude<DoclingVlmMode, ''>, number> = {
  verify: 1,
  full: 2,
  ocr: 3,
};

/** Whether a lane is reachable at the given tier level (display hint). */
export function laneAvailable(lane: Exclude<DoclingVlmMode, ''>, tierLevel: number): boolean {
  return tierLevel >= LANE_MIN_LEVEL[lane];
}
